/** Normalize backend topic_plan / nav payloads into SessionTopicPlan[]. */

import type { SessionTopicPlan, TopicTimingRecord } from "./types";
import {
  formatWallClockFromStart,
  parseFlexibleTimeToMinutes,
} from "./sessionEndTime";

type RawTopic = Record<string, unknown>;

function asNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Resolve workshop day index from topic fields or "Day N" text. */
export function topicWorkshopDayIndex(topic: RawTopic): number | null {
  const raw = topic.workshop_day_index ?? topic.workshopDayIndex;
  if (raw != null && raw !== "" && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  const text = `${topic.description || ""} ${topic.title || ""} ${topic.name || ""}`;
  const match = /\bday\s*(\d+)\b/i.exec(String(text));
  if (match) return Math.max(0, Number(match[1]) - 1);
  return null;
}

/**
 * Keep only topics for the active workshop day.
 * Topics with no day tag are kept only on day 0 (legacy).
 */
export function filterTopicPlanForWorkshopDay(
  raw: unknown,
  dayIndex: number | null | undefined,
): unknown {
  if (dayIndex == null || !Number.isFinite(Number(dayIndex))) return raw;
  if (!Array.isArray(raw)) return raw;
  const target = Number(dayIndex);
  return raw.filter((item) => {
    if (!item || typeof item !== "object") return false;
    const idx = topicWorkshopDayIndex(item as RawTopic);
    if (idx == null) return target === 0;
    return idx === target;
  });
}

function formatRelativeClock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function timelineLabels(
  cursorMin: number,
  durationMin: number,
  sessionStartTime?: string,
): { timelineStart: string; timelineEnd: string } {
  if (sessionStartTime && parseFlexibleTimeToMinutes(sessionStartTime) != null) {
    return {
      timelineStart: formatWallClockFromStart(sessionStartTime, cursorMin * 60),
      timelineEnd: formatWallClockFromStart(
        sessionStartTime,
        (cursorMin + durationMin) * 60,
      ),
    };
  }
  return {
    timelineStart: formatRelativeClock(cursorMin),
    timelineEnd: formatRelativeClock(cursorMin + durationMin),
  };
}

/** Distribute total minutes across n topics so the sum is exact. */
function distributeMinutes(total: number, count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(Math.max(0, total) / count);
  const rem = Math.max(0, total) - base * count;
  return Array.from({ length: count }, (_, i) => base + (i < rem ? 1 : 0)).map(
    (n) => Math.max(1, n || 1),
  );
}

/** Equal-slice topics from names — sum equals totalDurationMinutes. */
export function buildTopicsFromNames(
  names: string[],
  totalDurationMinutes = 90,
  sessionStartTime?: string,
): SessionTopicPlan[] {
  if (names.length === 0) return [];
  const slices = distributeMinutes(totalDurationMinutes, names.length);
  let cursor = 0;
  return names.map((name, index) => {
    const durationMin = slices[index] ?? 1;
    const start = cursor;
    cursor += durationMin;
    const labels = timelineLabels(start, durationMin, sessionStartTime);
    return {
      id: `topic-${index + 1}`,
      name,
      durationMin,
      timelineStart: labels.timelineStart,
      timelineEnd: labels.timelineEnd,
      completed: false,
    };
  });
}

export function normalizeTopicPlan(
  raw: unknown,
  fallbackTitle?: string,
  fallbackDurationMin = 60,
  sessionStartTime?: string,
): SessionTopicPlan[] {
  if (Array.isArray(raw) && raw.length > 0) {
    const fallbackSlices = distributeMinutes(
      fallbackDurationMin,
      raw.length,
    );
    let cursor = 0;
    return raw.map((item, index) => {
      const topic = (item || {}) as RawTopic;
      const durationMin = asNumber(
        topic.duration_minutes ?? topic.durationMinutes ?? topic.durationMin,
        fallbackSlices[index] ?? 15,
      );
      const start = cursor;
      cursor += durationMin;
      const id = String(topic.id ?? `topic-${index + 1}`);
      const name = String(
        topic.title ?? topic.name ?? topic.topic ?? `Topic ${index + 1}`,
      ).trim();
      const preQuiz = topic.pre_quiz ?? topic.preQuiz;
      const postQuiz = topic.post_quiz ?? topic.postQuiz;
      const labels = timelineLabels(start, durationMin, sessionStartTime);

      // Parse subtopics if present
      const rawSubs = (topic.subtopics ?? topic.sub_topics) as unknown;
      let subtopics:
        | Array<{
            id: string;
            name: string;
            durationMin: number;
            completed: boolean;
            hasPreQuiz?: boolean;
            hasPostQuiz?: boolean;
            skipped?: boolean;
            actualStartAt?: string;
            actualEndAt?: string;
            actualDurationSec?: number;
          }>
        | undefined;
      if (Array.isArray(rawSubs) && rawSubs.length > 0) {
        subtopics = rawSubs.map((s: Record<string, unknown>, si: number) => {
          const subPre = s.pre_quiz ?? s.preQuiz;
          const subPost = s.post_quiz ?? s.postQuiz;
          // Post-quiz is required per subtopic in the planner. Older plans
          // stored subtopics without flags — treat missing post as enabled.
          const hasPostQuiz =
            subPost === undefined || subPost === null
              ? true
              : Boolean(subPost);
          const actualDurationSec =
            s.actual_duration_sec != null &&
            Number.isFinite(Number(s.actual_duration_sec))
              ? Math.max(0, Number(s.actual_duration_sec))
              : s.actualDurationSec != null &&
                  Number.isFinite(Number(s.actualDurationSec))
                ? Math.max(0, Number(s.actualDurationSec))
                : undefined;
          return {
            id: String(s.id ?? `sub-${si}`),
            name:
              String(s.title ?? s.name ?? `Subtopic ${si + 1}`).trim() ||
              `Subtopic ${si + 1}`,
            durationMin: asNumber(
              s.duration_minutes ?? s.durationMinutes ?? s.durationMin,
              5,
            ),
            completed: Boolean(s.completed),
            hasPreQuiz: Boolean(subPre),
            hasPostQuiz,
            skipped:
              s.skipped === undefined ? undefined : Boolean(s.skipped),
            actualStartAt:
              typeof s.actual_start_at === "string"
                ? s.actual_start_at
                : typeof s.actualStartAt === "string"
                  ? s.actualStartAt
                  : undefined,
            actualEndAt:
              typeof s.actual_end_at === "string"
                ? s.actual_end_at
                : typeof s.actualEndAt === "string"
                  ? s.actualEndAt
                  : undefined,
            actualDurationSec,
          };
        });
      }

      return {
        id,
        name: name || `Topic ${index + 1}`,
        durationMin,
        timelineStart: String(topic.timelineStart ?? labels.timelineStart),
        timelineEnd: String(topic.timelineEnd ?? labels.timelineEnd),
        completed: Boolean(topic.completed),
        skipped:
          topic.skipped === undefined ? undefined : Boolean(topic.skipped),
        hasPreQuiz: Boolean(preQuiz),
        hasPostQuiz: Boolean(postQuiz),
        actualStartAt:
          typeof topic.actual_start_at === "string"
            ? topic.actual_start_at
            : typeof topic.actualStartAt === "string"
              ? topic.actualStartAt
              : undefined,
        actualEndAt:
          typeof topic.actual_end_at === "string"
            ? topic.actual_end_at
            : typeof topic.actualEndAt === "string"
              ? topic.actualEndAt
              : undefined,
        actualDurationSec:
          topic.actual_duration_sec != null &&
          Number.isFinite(Number(topic.actual_duration_sec))
            ? Math.max(0, Number(topic.actual_duration_sec))
            : topic.actualDurationSec != null &&
                Number.isFinite(Number(topic.actualDurationSec))
              ? Math.max(0, Number(topic.actualDurationSec))
              : undefined,
        ...(subtopics ? { subtopics } : {}),
      };
    });
  }

  const title = String(fallbackTitle || "").trim();
  if (title) {
    return buildTopicsFromNames(
      [title],
      fallbackDurationMin,
      sessionStartTime,
    );
  }
  return [];
}

export function applyTopicTimingRecords(
  topics: SessionTopicPlan[],
  records: TopicTimingRecord[] | undefined,
): SessionTopicPlan[] {
  if (!records?.length) return topics;
  const byId = new Map(records.map((r) => [String(r.topic_id), r]));
  return topics.map((t) => {
    const row = byId.get(t.id);
    if (!row) return t;
    const ended =
      row.actual_end_at != null ||
      row.actual_duration_sec != null ||
      Boolean(row.skipped);
    const skipped = Boolean(row.skipped);
    let subtopics = t.subtopics;
    if (t.subtopics?.length && Array.isArray(row.subtopics) && row.subtopics.length) {
      const subById = new Map(
        row.subtopics.map((s) => [String(s.subtopic_id), s]),
      );
      subtopics = t.subtopics.map((sub) => {
        const subRow = subById.get(sub.id);
        if (!subRow) return sub;
        const subEnded =
          subRow.actual_end_at != null ||
          subRow.actual_duration_sec != null ||
          Boolean(subRow.skipped);
        return {
          ...sub,
          actualStartAt: subRow.actual_start_at || sub.actualStartAt,
          actualEndAt: subRow.actual_end_at || sub.actualEndAt,
          actualDurationSec:
            subRow.actual_duration_sec != null
              ? Number(subRow.actual_duration_sec)
              : sub.actualDurationSec,
          completed: subEnded || sub.completed,
          skipped:
            "skipped" in subRow ? Boolean(subRow.skipped) : sub.skipped,
        };
      });
    }
    return {
      ...t,
      actualStartAt: row.actual_start_at || t.actualStartAt,
      actualEndAt: row.actual_end_at || t.actualEndAt,
      actualDurationSec:
        row.actual_duration_sec != null
          ? Number(row.actual_duration_sec)
          : t.actualDurationSec,
      completed: ended || t.completed,
      // Explicit false/true from timing wins; otherwise keep plan value.
      skipped: "skipped" in row ? skipped : t.skipped,
      ...(subtopics ? { subtopics } : {}),
    };
  });
}

const COMPLETED_KEY = (sessionId: string) =>
  `codelive-topic-completed:${sessionId}`;

export function loadCompletedTopicIds(sessionId: string): string[] {
  try {
    const raw = sessionStorage.getItem(COMPLETED_KEY(sessionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function saveCompletedTopicIds(
  sessionId: string,
  ids: string[],
): void {
  try {
    sessionStorage.setItem(COMPLETED_KEY(sessionId), JSON.stringify(ids));
  } catch {
    // ignore quota
  }
}

export function applyCompletedFlags(
  topics: SessionTopicPlan[],
  completedIds: string[],
): SessionTopicPlan[] {
  if (completedIds.length === 0) return topics;
  const set = new Set(completedIds);
  return topics.map((t) => ({
    ...t,
    completed: set.has(t.id) || t.completed,
    subtopics: t.subtopics?.map((s) => ({
      ...s,
      completed: set.has(s.id) || s.completed,
    })),
  }));
}

export function markTopicCompletedLocal(
  sessionId: string,
  topicId: string,
): string[] {
  const next = Array.from(
    new Set([...loadCompletedTopicIds(sessionId), topicId]),
  );
  saveCompletedTopicIds(sessionId, next);
  return next;
}
